import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const signup = async (username, email, password) => {
  const response = await api.post('/auth/signup', { username, email, password });
  return response.data;
};

export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// OAuth APIs
export const getOAuthUrl = async () => {
  const response = await api.get('/oauth/google');
  return response.data;
};

export const getEmailAccounts = async () => {
  const response = await api.get('/oauth/accounts');
  return response.data;
};

export const deleteEmailAccount = async (accountId) => {
  const response = await api.delete(`/oauth/accounts/${accountId}`);
  return response.data;
};

export const addAlias = async (accountId, aliasEmail, aliasName) => {
  const response = await api.post(`/oauth/accounts/${accountId}/aliases`, {
    alias_email: aliasEmail,
    alias_name: aliasName
  });
  return response.data;
};

export const deleteAlias = async (aliasId) => {
  const response = await api.delete(`/oauth/aliases/${aliasId}`);
  return response.data;
};

export const updateAccountLimit = async (accountId, limit) => {
  const response = await api.put(`/oauth/accounts/${accountId}/limit`, {
    daily_email_limit: limit
  });
  return response.data;
};

export const updateAliasLimit = async (aliasId, limit) => {
  const response = await api.put(`/oauth/aliases/${aliasId}/limit`, {
    daily_email_limit: limit
  });
  return response.data;
};

// Campaign APIs
export const createCampaign = async (campaignData, csvFile = null) => {
  const formData = new FormData();
  formData.append('campaignName', campaignData.campaignName);
  formData.append('subjectLine', campaignData.subjectLine);
  formData.append('htmlContent', campaignData.htmlContent);
  
  // Always send isTemplate FIRST (before recipientEmails) to ensure backend sees it
  if (campaignData.isTemplate !== undefined) {
    formData.append('isTemplate', String(campaignData.isTemplate));
    console.log('Sending isTemplate flag:', campaignData.isTemplate, 'as string:', String(campaignData.isTemplate));
  } else {
    formData.append('isTemplate', 'false');
  }
  
  if (csvFile) {
    formData.append('csvFile', csvFile);
  } else {
    // Always send recipientEmails, even if empty array (for template campaigns)
    const emails = campaignData.recipientEmails || [];
    formData.append('recipientEmails', JSON.stringify(emails));
    console.log('Sending recipientEmails:', emails, 'as JSON:', JSON.stringify(emails));
  }

  // Add response config if provided
  if (campaignData.responseConfig) {
    formData.append('responseConfig', JSON.stringify(campaignData.responseConfig));
  }

  // Log all form data keys for debugging
  console.log('FormData keys:', Array.from(formData.keys()));

  const response = await api.post('/campaigns/create', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const sendCampaign = async (campaignId, accountIds = null, aliasIds = null) => {
  const response = await api.post(`/campaigns/${campaignId}/send`, {
    accountIds,
    aliasIds
  });
  return response.data;
};

export const getCampaigns = async () => {
  const response = await api.get('/campaigns');
  return response.data;
};

export const getCampaignDetails = async (campaignId) => {
  const response = await api.get(`/campaigns/${campaignId}`);
  return response.data;
};

export const getCampaignAnalysis = async (campaignId) => {
  const response = await api.get(`/campaigns/${campaignId}/analysis`);
  return response.data;
};

export const updateCampaign = async (campaignId, campaignData) => {
  const response = await api.put(`/campaigns/${campaignId}`, {
    campaignName: campaignData.campaignName,
    subjectLine: campaignData.subjectLine,
    htmlContent: campaignData.htmlContent,
    responseConfig: campaignData.responseConfig
  });
  return response.data;
};

export const deleteCampaign = async (campaignId) => {
  const response = await api.delete(`/campaigns/${campaignId}`);
  return response.data;
};

// Legacy API
export const fetchTagResponses = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/tag-responses`);
    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(`Failed to fetch data: ${error.message}`);
  }
};

// Website scraping API (requires authentication)
export const scrapeWebsites = async (websites, fields) => {
  try {
    const response = await api.post(
      '/get-details/details',
      {
        websites: websites,
        fields: fields,
      },
      {
        timeout: 300000, // 5 minutes timeout
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 402) {
        throw new Error(error.response.data.message || 'Insufficient credits');
      } else if (error.response.status === 401) {
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(error.response.data.message || 'Server error occurred');
    } else if (error.request) {
      throw new Error('No response from server. Please check if the server is running.');
    } else {
      throw new Error(error.message || 'An error occurred');
    }
  }
};

// Get user's scraping history
export const getUserScrapingHistory = async (page = 1, limit = 20) => {
  const response = await api.get('/get-details/history', {
    params: { page, limit }
  });
  return response.data;
};

// Get all scraping history (admin only)
export const getAllScrapingHistory = async (page = 1, limit = 50) => {
  const response = await api.get('/get-details/history/all', {
    params: { page, limit }
  });
  return response.data;
};

// Get user credits
export const getUserCredits = async () => {
  const response = await api.get('/get-details/credits');
  return response.data;
};

// Admin User Management APIs
export const getAllUsers = async (page = 1, limit = 50) => {
  const response = await api.get('/admin/users', {
    params: { page, limit }
  });
  return response.data;
};

export const getUserById = async (userId) => {
  const response = await api.get(`/admin/users/${userId}`);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await api.put(`/admin/users/${userId}`, userData);
  return response.data;
};

export const updateUserCredits = async (userId, credits, operation = 'set', creditType = 'calling') => {
  const payload = { operation };
  if (creditType === 'validation') {
    payload.validation_credit = credits;
  } else {
    payload.calling_credit = credits;
  }
  payload.creditType = creditType;
  const response = await api.put(`/admin/users/${userId}/credits`, payload);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/admin/users/${userId}`);
  return response.data;
};

// Call Dispatch APIs
export const dispatchCall = async (toNumber, callContext = {}) => {
  const response = await api.post('/calls/dispatch', {
    to_number: toNumber,
    call_context: callContext
  });
  return response.data;
};

export const dispatchBulkCalls = async (phoneNumbers, callContext = {}, onProgress = null) => {
  const response = await api.post('/calls/dispatch-bulk', {
    to_numbers: phoneNumbers, // Array of phone numbers
    call_context: callContext
  }, {
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  });
  return response.data;
};

export const getCallStatus = async (callId) => {
  const response = await api.get(`/calls/status/${callId}`);
  return response.data;
};

export const getUserCallHistory = async (page = 1, limit = 20) => {
  const response = await api.get('/calls/history', {
    params: { page, limit }
  });
  return response.data;
};

export const getAllCallHistory = async (page = 1, limit = 50) => {
  const response = await api.get('/calls/history/all', {
    params: { page, limit }
  });
  return response.data;
};

export const getCallStats = async () => {
  const response = await api.get('/calls/stats');
  return response.data;
};

// Task Management APIs
export const createTask = async (taskData) => {
  const response = await api.post('/tasks', taskData);
  return response.data;
};

export const getUserTasks = async (status, userId = null) => {
  const params = {};
  if (status) params.status = status;
  if (userId) params.user_id = userId; // Admin: fetch another user's tasks
  const response = await api.get('/tasks', { params: Object.keys(params).length ? params : undefined });
  return response.data;
};

export const getTaskById = async (taskId) => {
  const response = await api.get(`/tasks/${taskId}`);
  return response.data;
};

export const getTaskRecordsForExport = async (taskId) => {
  const response = await api.get(`/tasks/${taskId}/records-export`);
  return response.data;
};

export const updateTask = async (taskId, taskData) => {
  console.log('🌐 API: updateTask called', {
    taskId,
    taskData,
    url: `/tasks/${taskId}`,
    method: 'PUT'
  });
  
  try {
    const response = await api.put(`/tasks/${taskId}`, taskData);
    console.log('🌐 API: updateTask response', {
      status: response.status,
      data: response.data
    });
    return response.data;
  } catch (error) {
    console.error('🌐 API: updateTask error', {
      error,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

export const deleteTask = async (taskId) => {
  const response = await api.delete(`/tasks/${taskId}`);
  return response.data;
};

export const uploadTaskRecords = async (taskId, records) => {
  const response = await api.post(`/tasks/${taskId}/records`, { records });
  return response.data;
};

export const updateTaskRecord = async (taskId, recordId, recordData) => {
  const response = await api.put(`/tasks/${taskId}/records/${recordId}`, recordData);
  return response.data;
};

export const updateRecordField = async (taskId, recordId, field, value) => {
  const response = await api.patch(`/tasks/${taskId}/records/${recordId}/field`, { field, value });
  return response.data;
};

export const deleteTaskRecord = async (taskId, recordId) => {
  const response = await api.delete(`/tasks/${taskId}/records/${recordId}`);
  return response.data;
};

// Task Operations APIs
export const verifyEmail = async (taskId, recordId) => {
  const response = await api.post(`/tasks/${taskId}/records/${recordId}/verify-email`);
  return response.data;
};

export const extractWebsite = async (taskId, recordId, fields) => {
  const response = await api.post(`/tasks/${taskId}/records/${recordId}/extract-website`, { fields });
  return response.data;
};

export const makeCall = async (taskId, recordId, callContext) => {
  const response = await api.post(`/tasks/${taskId}/records/${recordId}/call`, { call_context: callContext });
  return response.data;
};

export const getCallDetails = async (taskId, recordId) => {
  const response = await api.get(`/tasks/${taskId}/records/${recordId}/call-details`);
  return response.data;
};

export const bulkOperations = async (taskId, recordIds, operation, campaignId = null) => {
  const payload = {
    record_ids: recordIds,
    operation: operation.trim() // Ensure no extra spaces
  };
  
  if (operation === 'send-email' && campaignId) {
    payload.campaign_id = campaignId;
  }
  
  console.log('📤 API: bulkOperations payload:', payload);
  
  const response = await api.post(`/tasks/${taskId}/bulk-operations`, payload);
  return response.data;
};

// Task Email APIs
export const scrapeLinkedInProfile = async (taskId, recordId) => {
  const response = await api.post(`/tasks/${taskId}/records/${recordId}/scrape-linkedin-profile`);
  return response.data;
};

export const scrapeLinkedInCompany = async (taskId, recordId) => {
  const response = await api.post(`/tasks/${taskId}/records/${recordId}/scrape-linkedin-company`);
  return response.data;
};

export const sendTaskEmail = async (taskId, recordId, campaignId, accountIds, aliasIds, customSubjectLine) => {
  const response = await api.post(`/tasks/${taskId}/records/${recordId}/send-email`, {
    campaign_id: campaignId,
    accountIds,
    aliasIds,
    custom_subject_line: customSubjectLine || null
  });
  return response.data;
};

// Bulk Email with custom subject line
export const sendBulkEmails = async (taskId, campaignId, customSubjectLine, recordIds = null, filter = null) => {
  const response = await api.post(`/tasks/${taskId}/send-bulk-emails`, {
    campaign_id: campaignId,
    custom_subject_line: customSubjectLine,
    record_ids: recordIds,
    filter
  });
  return response.data;
};

// Validation APIs
export const uploadValidationRules = async (taskId, validationRules, fileName) => {
  const response = await api.post(`/tasks/${taskId}/validation-rules`, {
    validation_rules: validationRules,
    file_name: fileName
  });
  return response.data;
};

export const getValidationRules = async (taskId) => {
  const response = await api.get(`/tasks/${taskId}/validation-rules`);
  return response.data;
};

export const validateSingleRecord = async (taskId, recordId) => {
  const response = await api.post(`/tasks/${taskId}/records/${recordId}/validate`);
  return response.data;
};

export const validateAllRecords = async (taskId) => {
  const response = await api.post(`/tasks/${taskId}/validate-all`);
  return response.data;
};

export const validateSelectedRecords = async (taskId, recordIds, filter = null) => {
  const response = await api.post(`/tasks/${taskId}/validate-selected`, { 
    recordIds: recordIds || [], 
    filter: filter || null
  });
  return response.data;
};

export const getValidationStatus = async (taskId) => {
  const response = await api.get(`/tasks/${taskId}/validation-status`);
  return response.data;
};

export const cancelValidation = async (taskId) => {
  const response = await api.post(`/tasks/${taskId}/validation-cancel`);
  return response.data;
};

export const pauseValidation = async (taskId) => {
  const response = await api.post(`/tasks/${taskId}/validation-pause`);
  return response.data;
};

export const resumeValidation = async (taskId) => {
  const response = await api.post(`/tasks/${taskId}/validation-resume`);
  return response.data;
};

export const getValidationProgress = async (taskId, recordId) => {
  const response = await api.get(`/tasks/${taskId}/records/${recordId}/validation-progress`);
  return response.data;
};

// Retell APIs
export const getRetellAgents = async () => {
  const response = await api.get('/retell/agents');
  return response.data;
};

export const getRetellPhoneNumbers = async () => {
  const response = await api.get('/retell/phone-numbers');
  return response.data;
};

export default api;

